import React, { useEffect, useState, useContext } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";

import {
  getAllStatesByCountry,
  getAllIdTypes,
  getAllCountries,
  getAllSexTypes,
  createClient,
  getClientLevels,
  getClientSegmentsFiltered,
} from "../../../api/Services";

import {
  Button,
  DatePicker,
  Form,
  Input,
  message,
  Select,
  Spin,
  Switch,
} from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { RxPerson } from "react-icons/rx";
import CustomBreadcrumb from "../../../components/breadcrumb/Breadcrumb";

import styles from "./NewClient.module.scss";

const NewClient = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const getBasePath = () => {
    if (location.pathname.startsWith("/fidelity")) {
      return "/fidelity/clients";
    }
    return "/commercial-intelligence/clients?page=1";
  };

  const [loading, setLoading] = useState(false);
  const [countries, setCountries] = useState<any[]>([]);
  const [statesByCountry, setStatesByCountry] = useState<any[]>([]);
  const [sexTypes, setSexTypes] = useState<any[]>([]);
  const [idTypes, setIdTypes] = useState<any[]>([]);
  const [clientLevels, setClientLevels] = useState<any[]>([]);
  const [clientSegments, setClientSegments] = useState<any[]>([]);

  const [form] = Form.useForm();
  const [metricsForm] = Form.useForm();

  useEffect(() => {
    handleFetchIdTypes();
    handleFetchSexTypes();
    handleFetchCountries();
    handleFetchClientLevels();
    handleSearchClientSegments("");
  }, []);

  const handleFetchIdTypes = async () => {
    const response = await getAllIdTypes();
    setIdTypes(response);
  };

  const handleFetchCountries = async () => {
    const response = await getAllCountries();
    setCountries(response);
  };

  const handleFetchSexTypes = async () => {
    const response = await getAllSexTypes();
    setSexTypes(response);
  };

  const handleFetchClientLevels = async () => {
    const { clientLevels } = await getClientLevels(1, {});
    setClientLevels(clientLevels);
  };

  const handleSearchClientSegments = async (value: string) => {
    const response = await getClientSegmentsFiltered(1, { name: value });
    setClientSegments(response.clientSegments);
  };


  const getCountriesOptions = (items: any) => {
    const options: any[] = [];

    items?.forEach(function (item: any) {
      options.push(
        <Select.Option value={item.id} key={item.id}>
          {item.name}
        </Select.Option>
      );
    });
    return options;
  };

  const handleFetchStatesByCountry = async (countryId: number) => {
    const response = await getAllStatesByCountry(countryId);
    setStatesByCountry(response);
  };

  const getIdTypesOptions = (items: any) => {
    const options: any[] = [];

    items?.forEach(function (item: any) {
      options.push(
        <Select.Option value={item.id} key={item.id}>
          {item.descriptionEs}
        </Select.Option>
      );
    });
    return options;
  };

  const getSexTypesOptions = (items: any) => {
    const options: any[] = [];

    items?.forEach(function (item: any) {
      options.push(
        <Select.Option value={item.value} key={item.value}>
          {item.description_es}
        </Select.Option>
      );
    });
    return options;
  };

  const handleCreate = async () => {
    try {
      const [data, metricsData] = await Promise.all([
        form.validateFields(),
        metricsForm.validateFields()
      ]);
      
      setLoading(true);
      
      if (data.birthdate) data.birthdate = data.birthdate.format("YYYY-MM-DD");

      const {
        idNumber,
        birthdate,
        sex,
        country,
        city,
        documentType,
        active,
        name,
        lastName,
        phone,
        mobile,
        address,
        email,
        storeOnlyContactValidation,
      } = data;

      const {
        levelId,
        segmentIds,
        termsConditions,
      } = metricsData;

      const response = await createClient({
        idNumber,
        documentType,
        birthdate,
        sex,
        country,
        city,
        active,
        levelId,
        segments: segmentIds,
        name,
        lastName,
        phone,
        mobile,
        address,
        email,
        storeOnlyContactValidation,
        termsConditions,
      });
      
      if (response) {
        message.success(t("Common-CreateSuccessMessage"));
        form.resetFields();
        metricsForm.resetFields();
        navigate(getBasePath());
      } else message.error(t("Common-ErrorMessage"));

      setLoading(false);
    } catch (error) {
      message.error(t("Common-ValidationErrorMessage"));
    }
  };

  return loading ? (
    <Spin
      indicator={<LoadingOutlined className="ol-loading-spin-icon" />}
      delay={500}
    />
  ) : (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <div>
          <CustomBreadcrumb
            items={[
              {
                icon: <RxPerson />,
                label: t("Common-Clients"),
                path: getBasePath()
              },
              {
                label: t("Common-NewClient"),
                isCurrentPage: true
              }
            ]}
          />
        </div>
        <div className={styles.actionButtonsWrapper}>
          <Button
            onClick={handleCreate}
            className={styles.saveButton}
            type="primary"
          >
            {t("Common-Add")}
          </Button>
        </div>
      </div>
      <div className={styles.contentWrapper}>
        <div className={styles.clientInfoWrapper}>
          <Form
            id="newClient"
            name="newClient"
            layout="vertical"
            initialValues={{ remember: true, active: false, enable: false }}
            autoComplete="off"
            form={form}
            className={styles.newForm}
            onFinish={handleCreate}
          >
            <div className={styles.sectionTitle}>{t("Common-PersonalInfo")}</div>
            <div className={styles.itemWrapper}>
              <Form.Item
                className={styles.formItem}
                label={t("Common-Identifier")}
                name="idNumber"
                rules={[
                  {
                    required: true,
                    message: t("Common-Identifier"),
                  },
                ]}
              >
                <Input placeholder={t("Common-Identifier")} />
              </Form.Item>
            </div>
            <div className={styles.itemWrapper}>
              <Form.Item
                className={styles.formItem}
                label={t("Common-DocumentType")}
                name="documentType"
              >
                <Select placeholder={t("Common-DocumentType")} allowClear>
                  {getIdTypesOptions(idTypes)}
                </Select>
              </Form.Item>
              <Form.Item 
                className={styles.formItem}
                label={t("Common-Sex")} 
                name="sex"
              >
                <Select placeholder={t("Common-Sex")} allowClear>
                  {getSexTypesOptions(sexTypes)}
                </Select>
              </Form.Item>
            </div>
            <div className={styles.itemWrapper}>
              <Form.Item 
                className={styles.formItem}
                label={t("Common-BirthDate")} 
                name="birthdate"
              >
                <DatePicker 
                  className={styles.dateSelector} 
                  format="YYYY-MM-DD" 
                  placeholder={t("Common-SelectDate")}
                />
              </Form.Item>
              <Form.Item
                className={styles.formItem}
                label={t("Common-Active")}
                name="active"
                rules={[
                  {
                    required: true,
                    message: t("Common-Active"),
                  },
                ]}
              >
                <Switch />
              </Form.Item>
            </div>
            <div className={styles.sectionDivider} />
            <div className={styles.sectionTitle}>{t("Common-Location")}</div>
            <div className={styles.itemWrapper}>
              <Form.Item
                className={styles.formItem}
                label={t("Common-Country")}
                name="country"
              >
                <Select
                  placeholder={t("Common-Country")}
                  onChange={(value: any) => {
                    handleFetchStatesByCountry(value);
                  }}
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input: string, option: any) =>
                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                >
                  {getCountriesOptions(countries)}
                </Select>
              </Form.Item>
              <Form.Item
                className={styles.formItem}
                label={t("Common-City")}
                name="city"
              >
                <Select
                  placeholder={t("Common-City")}
                  filterOption={(input: string, option: any) =>
                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                  showSearch
                  optionFilterProp="children"
                >
                  {getCountriesOptions(statesByCountry)}
                </Select>
              </Form.Item>
            </div>
            <div className={styles.itemWrapper}>
              <Form.Item
                className={styles.formItem}
                label={t("Common-Address")}
                name="address"
              >
                <Input placeholder={t("Common-Address")} />
              </Form.Item>
              <div className={styles.formItem}></div>
            </div>
            <div className={styles.sectionDivider} />
            <div className={styles.sectionTitle}>{t("Common-Contact")}</div>
            <div className={styles.itemWrapper}>
              <Form.Item
                className={styles.formItem}
                label={t("Common-Name")}
                name="name"
              >
                <Input placeholder={t("Common-Name")} />
              </Form.Item>
              <Form.Item
                className={styles.formItem}
                label={t("Common-LastName")}
                name="lastName"
              >
                <Input placeholder={t("Common-LastName")} />
              </Form.Item>
            </div>
            <div className={styles.itemWrapper}>
              <Form.Item
                className={styles.formItem}
                label={t("Common-Phone")}
                name="phone"
              >
                <Input placeholder={t("Common-Phone")} />
              </Form.Item>
              <Form.Item
                className={styles.formItem}
                label={t("Common-Mobile")}
                name="mobile"
              >
                <Input placeholder={t("Common-Mobile")} />
              </Form.Item>
            </div>
            <div className={styles.itemWrapper}>
              <Form.Item
                className={styles.formItem}
                label={t("Common-Email")}
                name="email"
              >
                <Input placeholder={t("Common-Email")} type="email" />
              </Form.Item>
              <div className={styles.formItem}></div>
            </div>
            <div className={styles.itemWrapper}>
              <Form.Item
                className={styles.formItem}
                label={t("Common-StoreOnlyContactValidation")}
                name="storeOnlyContactValidation"
                valuePropName="checked"
                initialValue={false}
              >
                <Switch />
              </Form.Item>
              <div className={styles.formItem}></div>
            </div>
          </Form>
        </div>
        <div className={styles.clientMetricsWrapper}>
          <div className={styles.sectionTitle}>{t("Common-Metrics")}</div>
          <Form
            form={metricsForm}
            name="metrics"
            layout="vertical"
            initialValues={{ remember: true }}
            autoComplete="off"
            className={styles.newForm}
          >
            <div className={styles.itemWrapper}>
              <Form.Item
                className={styles.formItem}
                label={t("Common-Level")}
                name="levelId"
              >
                <Select placeholder={t("Common-Level")}
                  showSearch
                  optionFilterProp="children"
                >
                  {clientLevels.map((level: any) => (
                    <Select.Option key={level.id} value={level.id}>
                      {level.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item
                className={styles.formItem}
                label={t("Common-Segments")}
                name="segmentIds"
              >
                <Select
                  mode="multiple"
                  placeholder={t("Common-Segments")}
                  showSearch
                  onSearch={handleSearchClientSegments}
                  filterOption={(input: string, option: any) =>
                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                >
                  {clientSegments.map((segment: any) => (
                    <Select.Option key={segment.id} value={segment.id}>
                      {segment.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </div>
            <div className={styles.itemWrapper}>
              <Form.Item 
                className={styles.formItem}
                label={t("Common-PointsBenefit")} 
                name="points"
              >
                <Input placeholder={t("Common-PointsBenefit")} />
              </Form.Item>
              <Form.Item
                className={styles.formItem}
                initialValue={false}
                label={t("Common-Status")}
                name="enable"
              >
                <Switch />
              </Form.Item>
            </div>
            <div className={styles.itemWrapper}>
              <Form.Item
                className={styles.formItem}
                label={t("Common-TermsConditions")}
                name="termsConditions"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
              <div className={styles.formItem}></div>
            </div>
          </Form>
        </div>
      </div>
    </div>
  );
};
export default NewClient;
